// src/features/media/components/VideosPage.tsx

import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Stack, Title, Text, Loader, Alert, Collapse } from '@mantine/core';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { useVideos } from '../useVideos';
import { MediaApi } from '../MediaApi';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { usePlayers } from '@/features/players/usePlayers';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTags } from '@/features/tag/useTags';
import { MediaToolbar } from './MediaToolbar';
import { MediaGrid } from './MediaGrid';
import { PublishModal } from './PublishModal';
import { MergeVideosModal } from './MergeVideosModal';
import { BatchTagModal } from '@/shared/components/BatchTagModal';
import type { BatchTagItem, BatchTagMode, BatchTagPlan } from '@/features/tag/TagBatchUtils';
import { VideoFilterPanel } from './VideoFilterPanel';
import { VideoFilterChips } from './VideoFilterChips';
import { VideoFilterUtils } from '../videoFilters';
import { useSelection } from '@/shared/contexts/SelectionContext';
import {
  useActivateVideos,
  useDeactivateVideos,
  useDeleteVideos,
  useBatchUpdateVideoTags,
} from '../useVideoMutations';
import { useMergeTrackingStore, MergeToast } from '../stores/mergeTrackingStore';
import { useVideoGridEngine } from '../hooks/useVideoGridEngine';
import { ManageTagsDrawer } from '@/features/tag/components/ManageTagsDrawer';
import { useVideoGridState } from '../stores/videoGridState';
import {
  MEDIA_QUERY_KEYS,
  MERGE_MIN_VIDEOS,
  VIDEO_DETAIL_PREFETCH_MAX_ROWS,
} from '@/lib/constants/media';
import { SearchDebounce } from '@/shared/utils/searchDebounce';
import { markEnd, markStart } from '@/shared/utils/perf';
import { useJobData } from '@/shared/hooks/useJobData';
import { JobProgressUtils, PROCESSING_MEDIA_STATUSES } from '@/shared/utils/jobProgress';
import type { VideoViewData, VideoGridRow, VideoSortKey } from '@/lib/types/Media';

// SelectionProvider is in VideosLayout (parent route), so we have context access here
export function VideosPage() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { videos, isLoading: videosLoading, isError, error } = useVideos();
  const { data: linkConfig, isLoading: linkLoading } = useSharedLinkConfig();
  const { defaultPlayerId, isLoading: playersLoading } = usePlayers();
  const { selectedIds, clearSelection } = useSelection();

  const searchText = useVideoGridState((s) => s.searchText);
  const setSearchText = useVideoGridState((s) => s.setSearchText);
  const searchMode = useVideoGridState((s) => s.searchMode);
  const setSearchMode = useVideoGridState((s) => s.setSearchMode);
  const filters = useVideoGridState((s) => s.filters);
  const setFilters = useVideoGridState((s) => s.setFilters);
  const clearFilters = useVideoGridState((s) => s.clearFilters);
  const filtersOpen = useVideoGridState((s) => s.filtersOpen);
  const setFiltersOpen = useVideoGridState((s) => s.setFiltersOpen);
  const sortKey = useVideoGridState((s) => s.sortKey);
  const sortDirection = useVideoGridState((s) => s.sortDirection);
  const setSort = useVideoGridState((s) => s.setSort);
  const setScrollTop = useVideoGridState((s) => s.setScrollTop);
  const awaitingBackNavPaint = useVideoGridState((s) => s.awaitingBackNavPaint);
  const completeBackNavPaint = useVideoGridState((s) => s.completeBackNavPaint);

  const activateMutation = useActivateVideos();
  const deactivateMutation = useDeactivateVideos();
  const deleteMutation = useDeleteVideos();

  const [publishVideo, setPublishVideo] = useState<VideoViewData | null>(null);
  const initialScrollTopRef = useRef(useVideoGridState.getState().scrollTop);
  const hasReportedFirstRowsPaintRef = useRef(false);
  const pendingSortPaintRef = useRef(false);
  const prefetchedDetailIdsRef = useRef(new Set<number>());

  // --- Merge state ---
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSourceIds, setMergeSourceIds] = useState<number[]>([]);
  // Pending merges live in the app-wide store; polling + toast resolution
  // run at the AppShell root so they survive leaving this page.
  const addMerge = useMergeTrackingStore((s) => s.addMerge);

  // --- Batch tags state ---
  const [batchTagOpen, setBatchTagOpen] = useState(false);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const { tags: allTags } = useTags();
  const batchTagMutation = useBatchUpdateVideoTags();

  const isLoading = videosLoading || linkLoading || playersLoading;

  // --- Processing percent in the grid ---

  const hasProcessingVideos = useMemo(
    () => videos.some((v) => PROCESSING_MEDIA_STATUSES.includes(v.media.mediaStatusType)),
    [videos],
  );

  // Poll jobs only while transient media exist (same 5s query the upload
  // panel uses, deduped on the shared query key). Interval pauses when the
  // tab is backgrounded (TanStack default).
  const { data: jobData } = useJobData(selectedCId, hasProcessingVideos);
  const processingProgress = useMemo(
    () => JobProgressUtils.buildMediaProgressMap(jobData),
    [jobData],
  );

  // When a tracked job finishes or hands off between phases, refetch the
  // list so mediaStatusType flips (Ingesting → Processing → Active/Inactive)
  // without waiting for staleTime. Phase is part of the diff signature: a
  // bare id-set diff missed the ingest→transcode handoff (the mediaId never
  // leaves the map) and the grid stayed "Uploading" through the transcode.
  const prevPhasesRef = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    const prev = prevPhasesRef.current;
    const current = JobProgressUtils.phaseSignatures(processingProgress);
    prevPhasesRef.current = current;
    if (selectedCId != null && JobProgressUtils.hasPhaseTransition(prev, current)) {
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
    }
  }, [processingProgress, queryClient, selectedCId]);

  useEffect(() => {
    markStart('videos.grid.firstRowsPaint');
  }, []);

  useEffect(() => {
    prefetchedDetailIdsRef.current.clear();
  }, [selectedCId]);

  // Escape key clears selection
  useHotkeys([
    ['Escape', () => clearSelection()],
  ]);

  // Typed text stays immediate in the store; only result recompute is deferred.
  const [debouncedSearchText] = useDebouncedValue(searchText, SearchDebounce.forItemCount(videos.length));

  const activeFilterCount = useMemo(() => VideoFilterUtils.countActive(filters), [filters]);

  const gridEngine = useVideoGridEngine({
    customerId: selectedCId,
    videos,
    sort: {
      key: sortKey,
      direction: sortDirection,
    },
    search: {
      mode: searchMode,
      text: debouncedSearchText,
    },
    filters,
    linkConfig,
    defaultPlayerId,
  });

  const videosById = useMemo(
    () => new Map(videos.map((video) => [video.media.mediaId, video])),
    [videos]
  );

  // Selections survive search/filter changes; surface how many selected rows
  // the current search/filters are hiding so the count never looks "wrong".
  const hiddenSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    const visible = new Set(gridEngine.orderedIds);
    let hidden = 0;
    for (const id of selectedIds) {
      if (!visible.has(id)) hidden++;
    }
    return hidden;
  }, [selectedIds, gridEngine.orderedIds]);

  const handleSort = useCallback(
    (key: VideoSortKey) => {
      pendingSortPaintRef.current = true;
      markStart('videos.grid.sort.total');
      setSort(key);
    },
    [setSort]
  );

  // --- Bulk action handlers ---

  const handleBulkActivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    activateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Activated', message: `${ids.length} video(s) activated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, activateMutation, clearSelection]);

  const handleBulkDeactivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    deactivateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Deactivated', message: `${ids.length} video(s) deactivated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, deactivateMutation, clearSelection]);

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    modals.openConfirmModal({
      title: 'Delete Videos',
      children: (
        <Text size="sm">
          Are you sure you want to delete {ids.length} video(s)? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate(ids, {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `${ids.length} video(s) deleted.`, color: 'green' });
            clearSelection();
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [selectedIds, deleteMutation, clearSelection]);

  // --- Batch tags handlers ---

  // Live view of the selection for the modal (selection can't change while a
  // modal is open; list refetches keep the plan preview current).
  const batchTagItems = useMemo<BatchTagItem[]>(() => {
    if (!batchTagOpen) return [];
    const items: BatchTagItem[] = [];
    for (const id of selectedIds) {
      const video = videosById.get(id);
      if (video) {
        items.push({ id, title: video.media.title, tags: video.tags ?? [] });
      }
    }
    return items;
  }, [batchTagOpen, selectedIds, videosById]);

  const handleBatchTagApply = useCallback((plan: BatchTagPlan, mode: BatchTagMode, tags: string[]) => {
    // Send only the ids the plan says will actually change — the server does
    // the set math (add = ensure present, remove = ensure absent), so a
    // retry after an error is always safe.
    batchTagMutation.mutate(
      {
        mediaIds: plan.changed.map((entry) => entry.id),
        addTags: mode === 'add' ? tags : [],
        removeTags: mode === 'remove' ? tags : [],
      },
      {
        onSuccess: (result) => {
          setBatchTagOpen(false);
          clearSelection();
          const verb = mode === 'add' ? 'added to' : 'removed from';
          const tagLabel = tags.length === 1 ? `Tag "${tags[0]}"` : `${tags.length} tags`;
          notifications.show({
            title: mode === 'add' ? 'Tags Added' : 'Tags Removed',
            message: `${tagLabel} ${verb} ${result.updatedMediaCount} video(s).${
              plan.overLimit.length > 0 ? ` ${plan.overLimit.length} skipped (tag limit).` : ''
            }`,
            color: 'green',
          });
        },
        onError: (err) => {
          // Keep the modal open — applying again is idempotent server-side.
          notifications.show({
            title: 'Error',
            message: `${err.message} — you can apply again to retry.`,
            color: 'red',
            autoClose: 8000,
          });
        },
      },
    );
  }, [batchTagMutation, clearSelection]);

  // --- Merge handlers ---

  const handleOpenMerge = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length < MERGE_MIN_VIDEOS) return;

    // Check all selected videos are Ready
    const notReady = ids.filter(id => {
      const video = videosById.get(id);
      return !video || video.media.mediaStatusType !== MediaStatusType.Ready;
    });

    if (notReady.length > 0) {
      notifications.show({
        title: 'Cannot Merge',
        message: 'Only videos that are processed and ready can be merged.',
        color: 'red',
      });
      return;
    }

    setMergeSourceIds(ids);
    setMergeModalOpen(true);
  }, [selectedIds, videosById]);

  const handleMergeSuccess = useCallback((newVideo: VideoViewData, sourceCount: number, title: string) => {
    setMergeModalOpen(false);
    clearSelection();

    // Show initial notification (will be updated by job polling).
    // bottom-right: this toast is persistent for the duration of the merge
    // job — top-right would sit over modal close buttons and row actions
    // while the user keeps working.
    notifications.show({
      id: MergeToast.id(newVideo.media.mediaId),
      title: 'Merging...',
      message: `Merging ${sourceCount} videos into "${title}"...`,
      color: 'blue',
      loading: true,
      autoClose: false,
      position: 'bottom-right',
    });

    // Register pending merge for the app-root job polling
    addMerge({ mediaId: newVideo.media.mediaId, title });
  }, [clearSelection, addMerge]);

  // --- Single-row action handlers ---

  const handlePublish = useCallback((gridRow: VideoGridRow) => {
    const fullVideo = videosById.get(gridRow.mediaId);
    if (fullVideo) setPublishVideo(fullVideo);
  }, [videosById]);

  const handleRowActivateToggle = useCallback((video: VideoGridRow) => {
    const mutation = video.isActive ? deactivateMutation : activateMutation;
    const action = video.isActive ? 'Deactivated' : 'Activated';
    mutation.mutate([video.mediaId], {
      onSuccess: () => {
        notifications.show({ title: action, message: `"${video.title}" ${action.toLowerCase()}.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [activateMutation, deactivateMutation]);

  const handleRowDelete = useCallback((video: VideoGridRow) => {
    // Confirmation handled in MediaGrid's shared menu
    deleteMutation.mutate([video.mediaId], {
      onSuccess: () => {
        notifications.show({ title: 'Deleted', message: `"${video.title}" deleted.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [deleteMutation]);

  const handleFirstRowsPaint = useCallback(() => {
    if (!hasReportedFirstRowsPaintRef.current) {
      hasReportedFirstRowsPaintRef.current = true;
      markEnd('videos.grid.firstRowsPaint', {
        count: gridEngine.count,
      });
    }

    if (pendingSortPaintRef.current) {
      pendingSortPaintRef.current = false;
      markEnd('videos.grid.sort.total', {
        count: gridEngine.count,
        key: sortKey,
        direction: sortDirection,
      });
    }

    if (!awaitingBackNavPaint) return;
    markEnd('videos.grid.backnav.firstRowsPaint', {
      count: gridEngine.count,
    });
    completeBackNavPaint();
  }, [
    awaitingBackNavPaint,
    completeBackNavPaint,
    gridEngine.count,
    sortDirection,
    sortKey,
  ]);

  const handlePrefetchDetail = useCallback(
    (video: VideoGridRow) => {
      if (videos.length > VIDEO_DETAIL_PREFETCH_MAX_ROWS) return;
      if (selectedCId == null) return;
      if (prefetchedDetailIdsRef.current.has(video.mediaId)) return;

      prefetchedDetailIdsRef.current.add(video.mediaId);

      void queryClient.prefetchQuery({
        queryKey: MEDIA_QUERY_KEYS.video(selectedCId, video.mediaId),
        queryFn: () => MediaApi.getVideo(selectedCId, video.mediaId),
        staleTime: 60 * 1000,
      });

      void router.preloadRoute({
        to: '/videos/$id',
        params: { id: video.displayId },
      }).catch(() => {});
    },
    [selectedCId, queryClient, router, videos.length]
  );

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading videos...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load videos'}
      </Alert>
    );
  }

  if (!linkConfig) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Failed to load shared link configuration
      </Alert>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ flex: 1, minHeight: 0 }}>
      <Title order={2}>Videos</Title>

      <MediaToolbar
        searchText={searchText}
        onSearchChange={setSearchText}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        filterCount={activeFilterCount}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen(!filtersOpen)}
        totalCount={videos.length}
        resultCount={gridEngine.resultCount}
        hiddenSelectedCount={hiddenSelectedCount}
        onBulkActivate={handleBulkActivate}
        onBulkDeactivate={handleBulkDeactivate}
        onBulkDelete={handleBulkDelete}
        onBatchTags={() => setBatchTagOpen(true)}
        onManageTags={() => setManageTagsOpen(true)}
        onMerge={handleOpenMerge}
        activateLoading={activateMutation.isPending}
        deactivateLoading={deactivateMutation.isPending}
        deleteLoading={deleteMutation.isPending}
      />

      {activeFilterCount > 0 && (
        <VideoFilterChips filters={filters} onChange={setFilters} onClearAll={clearFilters} />
      )}

      <Collapse in={filtersOpen}>
        <VideoFilterPanel filters={filters} onChange={setFilters} />
      </Collapse>

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MediaGrid
          count={gridEngine.count}
          allIds={gridEngine.orderedIds}
          getRow={gridEngine.getRowByIndex}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onPublish={handlePublish}
          onActivateToggle={handleRowActivateToggle}
          onDelete={handleRowDelete}
          onPrefetchDetail={handlePrefetchDetail}
          processingProgress={processingProgress}
          initialScrollTop={initialScrollTopRef.current}
          onScrollTopChange={setScrollTop}
          onFirstVisibleRowsPaint={handleFirstRowsPaint}
        />
      </div>

      {publishVideo && (
        <PublishModal
          opened={!!publishVideo}
          onClose={() => setPublishVideo(null)}
          media={publishVideo}
        />
      )}

      {mergeModalOpen && (
        <MergeVideosModal
          opened={mergeModalOpen}
          onClose={() => setMergeModalOpen(false)}
          sourceVideoIds={mergeSourceIds}
          videosById={videosById}
          linkConfig={linkConfig}
          onSuccess={handleMergeSuccess}
        />
      )}

      <ManageTagsDrawer opened={manageTagsOpen} onClose={() => setManageTagsOpen(false)} />

      {batchTagOpen && (
        <BatchTagModal
          opened={batchTagOpen}
          onClose={() => setBatchTagOpen(false)}
          items={batchTagItems}
          entityLabel="video"
          allTags={allTags}
          isPending={batchTagMutation.isPending}
          onApply={handleBatchTagApply}
        />
      )}
    </Stack>
  );
}
